import { Button, Card, CardHeader, Grid, GridSize, IconButton, makeStyles, Theme, Typography } from '@material-ui/core';
import { AddCircle } from '@material-ui/icons';
import React, { ReactNode } from 'react';
import BlankStateImage from '../assets/blank-state.svg';

interface IGridSizesProps {
  shouldGiveExtraSpaceToText?: boolean;
  textSpaceOverride?: {
    textGridSize: GridSize;
    imageGridSize: GridSize;
  };
}

export interface IEmptyBodyOverlayProps extends IGridSizesProps {
  children?: ReactNode;
  cardInnerTitle?: string;
  cardInnerDescription?: ReactNode;
}
export interface EmptySetOverlayProps extends IEmptyBodyOverlayProps {
  cardHeaderTitle?: string;
  cardActionTitle?: string;
  cardActionHandler?(): unknown;
  showActionButtonInCardHeader?: boolean;
  cardActionDisabled?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    padding: theme.spacing(0, 4, 5),
    marginTop: theme.spacing(2),
  },
  bodyContainer: {
    textAlign: 'start',
  },
  button: {
    padding: theme.spacing(1, 2, 1, 2),
    margin: theme.spacing(4, 0, 0, 0),
  },
  body: {
    padding: theme.spacing(2, 0, 0, 0),
    fontSize: '14px',
  },
  image: {
    display: 'flex',
    alignItems: 'center',
    '& img': {
      marginLeft: '25%',
    },
    pointerEvents: 'all',
  },
  cardHeader: {
    borderBottom: '1px solid #D9E0E5',
  },
}));

const getGridSizes = ({ shouldGiveExtraSpaceToText, textSpaceOverride }: IGridSizesProps) => {
  let textGridSize: GridSize = 4;
  let imageGridSize: GridSize = 8;

  if (shouldGiveExtraSpaceToText) {
    textGridSize = 6;
    imageGridSize = 6;
  }

  if (textSpaceOverride) {
    textGridSize = textSpaceOverride.textGridSize;
    imageGridSize = textSpaceOverride.imageGridSize;
  }

  return { textGridSize, imageGridSize };
};

export const EmptyBodyOverlay = ({
  children,
  cardInnerDescription,
  cardInnerTitle,
  shouldGiveExtraSpaceToText,
  textSpaceOverride,
}: IEmptyBodyOverlayProps) => {
  const classes = useStyles();

  const { imageGridSize, textGridSize } = getGridSizes({ shouldGiveExtraSpaceToText, textSpaceOverride });

  return (
    <Grid container={true} className={classes.container} data-testid="container">
      <Grid item xs={textGridSize} className={classes.bodyContainer} data-testid="buttonContainer">
        {cardInnerTitle && (
          <Typography color="primary" variant="h5">
            {cardInnerTitle}
          </Typography>
        )}
        {cardInnerDescription && (
          <Typography className={classes.body} color="textPrimary" variant="body2" data-testid="textArea">
            {cardInnerDescription}
          </Typography>
        )}
        {children}
      </Grid>
      <Grid item={true} xs={imageGridSize} className={classes.image}>
        <img src={BlankStateImage} />
      </Grid>
    </Grid>
  );
};

export function EmptySetOverlay({
  cardHeaderTitle = '',
  cardActionTitle = '',
  cardActionHandler,
  showActionButtonInCardHeader = true,
  cardActionDisabled = false,
  ...emptyBodyOverlayProps
}: EmptySetOverlayProps) {
  const classes = useStyles();

  return (
    <Card>
      <CardHeader
        className={classes.cardHeader}
        title={cardHeaderTitle}
        action={
          cardActionHandler && showActionButtonInCardHeader ? (
            <IconButton
              onClick={cardActionHandler}
              color="primary"
              disabled={cardActionDisabled}
              data-testid="headerAddButton"
            >
              <AddCircle />
            </IconButton>
          ) : null
        }
      />
      <EmptyBodyOverlay {...emptyBodyOverlayProps}>
        {cardActionHandler && cardActionTitle && (
          <Button
            size="medium"
            startIcon={<AddCircle />}
            color="primary"
            className={classes.button}
            data-testid="add-button"
            component="span"
            onClick={cardActionHandler}
            disabled={cardActionDisabled}
          >
            {cardActionTitle}
          </Button>
        )}
      </EmptyBodyOverlay>
    </Card>
  );
}
